import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import AppIcon from '../../../components/AppIcon';


const RegistrationForm = ({ onSubmit, loading }) => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    subscribeNewsletter: false
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailConfirmationRequired, setEmailConfirmationRequired] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Calculate password strength
    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (/[A-Z]/?.test(password)) strength += 25;
    if (/[0-9]/?.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/?.test(password)) strength += 25;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength === 0) return '';
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return 'bg-error';
    if (passwordStrength <= 50) return 'bg-warning';
    if (passwordStrength <= 75) return 'bg-accent';
    return 'bg-success';
  };

  const validateForm = () => {
    const newErrors = {};

    // Full name validation
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Full name must be at least 2 characters';
    }

    // Email validation
    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength < 50) {
      newErrors.password = 'Password is too weak. Include uppercase, numbers, and symbols';
    }

    // Confirm password validation
    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Terms agreement validation
    if (!formData?.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Call real Supabase signUp
      const { data, error, requiresEmailConfirmation } = await signUp(
        formData?.email,
        formData?.password,
        {
          full_name: formData?.fullName,
          role: 'customer'
        }
      );

      if (error) {
        setErrors({ submit: error?.message });
        return;
      }

      // Handle email confirmation requirement
      if (requiresEmailConfirmation) {
        setEmailConfirmationRequired(true);
        return;
      }

      // If signup successful with immediate session, redirect to dashboard
      if (data?.user && data?.session) {
        navigate('/customer-dashboard');
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Email confirmation screen
  if (emailConfirmationRequired) {
    return (
      <div className="text-center space-y-6">
        <div className="w-16 h-16 bg-accent/20 rounded-full flex items-center justify-center mx-auto">
          <AppIcon name="Mail" size={32} className="text-accent" />
        </div>
        
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            Check Your Email
          </h3>
          <p className="text-muted-foreground">
            We've sent a confirmation link to <strong>{formData?.email}</strong>
          </p>
        </div>

        <div className="bg-accent/10 rounded-lg p-4 text-sm text-muted-foreground text-left">
          <p className="mb-2">
            <strong>Next steps:</strong>
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Check your email inbox (and spam folder)</li>
            <li>Click the confirmation link in the email</li>
            <li>Return here to sign in with your credentials</li>
          </ol>
        </div>

        <Button
          variant="outline"
          onClick={() => navigate('/login')}
          fullWidth
        >
          Go to Login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Full Name"
        type="text"
        name="fullName"
        value={formData?.fullName}
        onChange={handleChange}
        placeholder="Enter your full name"
        error={errors?.fullName}
        required
        className="mb-4"
      />
      <Input
        label="Email Address"
        type="email"
        name="email"
        value={formData?.email}
        onChange={handleChange}
        placeholder="Enter your email address"
        description="We'll use this to send you booking confirmations and updates"
        error={errors?.email}
        required
        className="mb-4"
      />
      <div className="space-y-2">
        <Input
          label="Password"
          type="password"
          name="password"
          value={formData?.password}
          onChange={handleChange}
          placeholder="Create a strong password"
          error={errors?.password}
          required
          className="mb-2"
        />
        
        {formData?.password && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Password strength:</span>
              <span className={`font-medium ${
                passwordStrength <= 25 ? 'text-error' :
                passwordStrength <= 50 ? 'text-warning' :
                passwordStrength <= 75 ? 'text-accent' : 'text-success'
              }`}>
                {getPasswordStrengthText()}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      <Input
        label="Confirm Password"
        type="password"
        name="confirmPassword"
        value={formData?.confirmPassword}
        onChange={handleChange}
        placeholder="Confirm your password"
        error={errors?.confirmPassword}
        required
        className="mb-4"
      />
      <div className="space-y-4">
        <Checkbox
          label="I agree to the Terms and Conditions and Privacy Policy"
          name="agreeToTerms"
          checked={formData?.agreeToTerms}
          onChange={handleChange}
          error={errors?.agreeToTerms}
          required
          description={
            <span className="text-sm text-muted-foreground">
              By creating an account, you agree to our{' '}
              <a href="/terms" className="text-primary hover:underline">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
            </span>
          }
        />

        <Checkbox
          label="Subscribe to newsletter for travel deals and updates"
          name="subscribeNewsletter"
          checked={formData?.subscribeNewsletter}
          onChange={handleChange}
          description="Get exclusive travel offers and destination inspiration (optional)"
        />
      </div>

      {errors?.submit && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
          <div className="flex">
            <AppIcon name="AlertCircle" size={20} className="text-destructive flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          </div>
        </div>
      )}

      <Button
        type="submit"
        variant="default"
        size="lg"
        loading={isSubmitting || loading}
        fullWidth
        className="mt-8"
      >
        Create Account
      </Button>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-primary hover:underline font-medium"
          >
            Sign in
          </button>
        </p>
      </div>
    </form>
  );
};

export default RegistrationForm;